import { AuthError, PostgrestError } from '@supabase/supabase-js';

export class AuthenticationError extends Error {
  status: number | string
  __isAuthError: true;

  constructor(message: string, public originalError: AuthError | PostgrestError) {
    super(message);
    this.name = 'AuthenticationError';
    this.status =  originalError?.code || 500;
    this.__isAuthError = true;
  }
}

export function convertError(error: unknown): AuthenticationError {
  if (error instanceof AuthenticationError) {
    return error;
  }

  // Handle Supabase Auth Errors
  if (isSupabaseAuthError(error)) {
    return new AuthenticationError(
      getAuthErrorMessage(error),
      error
    );
  }

  // Handle Supabase Database Errors
  if (isPostgrestError(error)) {
    return new AuthenticationError(
      getDatabaseErrorMessage(error),
      error as PostgrestError
    );
  }

  // Handle unknown errors
  return new AuthenticationError(
    'An unexpected error occurred',
    {
      message: 'Unknown error',
      status: 500,
      code: 'UNKNOWN_ERROR',
    } as AuthError
  );
}

function isSupabaseAuthError(error: unknown): error is AuthError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    'status' in error
  );
}

function isPostgrestError(error: unknown): error is PostgrestError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    'details' in error
  );
}

function getAuthErrorMessage(error: AuthError): string {
  switch (error.message) {
    case 'Invalid login credentials':
      return 'Invalid email or password';
    case 'Email not confirmed':
      return 'Please verify your email address';
    case 'Email already registered':
      return 'This email is already registered';
    case 'Password is too short':
      return 'Password must be at least 6 characters';
    case 'User not found':
      return 'Account not found';
    default:
      return error.message;
  }
}

function getDatabaseErrorMessage(error: PostgrestError): string {
  switch (error.code) {
    case '23505': // unique_violation
      return 'This record already exists';
    case '23503': // foreign_key_violation
      return 'Referenced record does not exist';
    case '42P01': // undefined_table
      return 'Database configuration error';
    default:
      return error.message;
  }
}
